/**
 * useTaskModuleType - Resolve o módulo de tarefas corrente pela rota
 *
 * O módulo de Tarefas foi reaproveitado para atender também Retenção e Jurídico.
 * A separação é feita pelo enum `task_type_enum` no banco (normal | retention | legal)
 * e, no frontend, pelo prefixo da URL:
 *
 *   /tenant/tasks/*      -> normal
 *   /tenant/retention/*  -> retention
 *   /tenant/legal/*      -> legal
 *
 * Qualquer outra rota (ex.: modais abertos pelo CRM, veículos, clientes) usa 'normal'.
 *
 * @version 1.3.0
 * RELEASE: 2026-10-08 v1.3.0 - Rotas /tenant/service-orders/* usam SERVICE_ORDER_MODULE (structure
 *          'service_orders'): as mesmas telas de Categorias/Fases leem as RPCs da OS.
 * RELEASE: 2026-08-21 v1.2.0 - Rótulos de UI por módulo (newLabel/subtitle/emptyHint)
 * RELEASE: 2026-08-21 v1.1.0 - Suporte a override via TaskModuleProvider (subabas nos modais)
 * RELEASE: 2026-08-21 v1.0.0 - Criação inicial (Fase 3 do plano Retenção/Jurídico)
 */

import { useLocation } from 'react-router-dom';
import { useTaskModuleOverride } from '@/contexts/TaskModuleContext';


/** Tipos de módulo suportados (espelha public.task_type_enum) */
export type TaskModuleType = 'normal' | 'retention' | 'legal';

/** Metadados de apresentação de cada módulo */
export interface TaskModuleMeta {
  /** Valor enviado para as RPCs (p_task_type) */
  type: TaskModuleType;
  /** Prefixo de rota do módulo */
  basePath: string;
  /** Rótulo exibido na UI */
  label: string;
  /** Rótulo no singular (usado em textos livres) */
  singular: string;
  /** v1.2.0 — rótulo do botão de criação, já com gênero correto */
  newLabel: string;
  /** v1.2.0 — subtítulo padrão da página de listagem/kanban */
  subtitle: string;
  /** v1.2.0 — texto do estado vazio ("Crie ...") */
  emptyHint: string;
  /**
   * v1.3.0 — origem da estrutura (categorias/fases/permissões). 'service_orders' faz os
   * hooks compartilhados chamarem as RPCs `*_service_order_*` em vez das de Tarefas.
   */
  structure?: 'tasks' | 'service_orders';
}

/** Mapa estático de módulos (evita recriação a cada render) */
export const TASK_MODULES: Record<TaskModuleType, TaskModuleMeta> = {
  normal: {
    type: 'normal',
    basePath: '/tenant/tasks',
    label: 'Tarefas',
    singular: 'Tarefa',
    newLabel: 'Nova Tarefa',
    subtitle: 'Gerencie suas tarefas e atividades',
    emptyHint: 'Crie sua primeira tarefa para começar',
  },
  retention: {
    type: 'retention',
    basePath: '/tenant/retention',
    label: 'Retenção',
    singular: 'Solicitação',
    newLabel: 'Nova Solicitação',
    subtitle: 'Gerencie as solicitações de retenção',
    emptyHint: 'Crie sua primeira solicitação para começar',
  },
  legal: {
    type: 'legal',
    basePath: '/tenant/legal',
    label: 'Jurídico',
    singular: 'Processo',
    newLabel: 'Novo Processo',
    subtitle: 'Gerencie os processos jurídicos',
    emptyHint: 'Crie seu primeiro processo para começar',
  },
};

/**
 * v1.3.0 — Ordem de Serviço reaproveita as telas de Tarefas com estrutura própria.
 * `type` fica 'normal' só para compatibilidade de tipos; nunca é enviado às RPCs da OS.
 */
export const SERVICE_ORDER_MODULE: TaskModuleMeta = {
  type: 'normal',
  basePath: '/tenant/service-orders',
  label: 'Ordens de Serviço',
  singular: 'Ordem de Serviço',
  newLabel: 'Nova OS',
  subtitle: 'Gerencie as ordens de serviço',
  emptyHint: 'Crie sua primeira ordem de serviço para começar',
  structure: 'service_orders',
};

/**
 * Deriva o módulo a partir de um pathname (função pura, testável).
 */
export function resolveTaskModuleFromPath(pathname: string): TaskModuleMeta {
  if (pathname.startsWith('/tenant/service-orders')) return SERVICE_ORDER_MODULE;
  if (pathname.startsWith('/tenant/retention')) return TASK_MODULES.retention;
  if (pathname.startsWith('/tenant/legal')) return TASK_MODULES.legal;
  return TASK_MODULES.normal;
}

/**
 * Hook principal: retorna os metadados do módulo corrente.
 *
 * v1.1.0 — quando existe um TaskModuleProvider acima na árvore (subabas dos modais de
 * Cliente/Negócio/Veículo/Chat), o override tem prioridade sobre a rota.
 */
export function useTaskModule(): TaskModuleMeta {
  const { pathname } = useLocation();
  const override = useTaskModuleOverride();
  if (override) return TASK_MODULES[override];
  return resolveTaskModuleFromPath(pathname);
}


/**
 * Atalho quando só o valor do enum interessa (parâmetro p_task_type das RPCs).
 */
export function useTaskModuleType(): TaskModuleType {
  return useTaskModule().type;
}

/** v1.3.0 — true quando a tela está no módulo Ordem de Serviço. */
export function useIsServiceOrderStructure(): boolean {
  return useTaskModule().structure === 'service_orders';
}
